"use client";

import { Button } from "@workspace/ui/components/button";
import { useEffect } from "react";

interface RouteErrorProps {
	error: Error & { digest?: string };
	reset: () => void;
	context: string;
	description: string;
	fallbackUrl: string;
	fallbackLabel: string;
}

export function RouteError({
	error,
	reset,
	context,
	description,
	fallbackUrl,
	fallbackLabel,
}: RouteErrorProps) {
	useEffect(() => {
		if (process.env.NODE_ENV === "development") {
			console.error(`${context} route error:`, error);
		}
	}, [error, context]);

	return (
		<div className="container mx-auto flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4 py-16">
			<div className="flex flex-col items-center gap-2 text-center">
				<h1 className="text-4xl font-bold tracking-tight">
					Something went wrong
				</h1>
				<p className="text-muted-foreground max-w-md text-lg">
					{description}
				</p>
			</div>

			<div className="flex gap-3">
				<Button onClick={() => reset()} variant="default" size="lg">
					Try again
				</Button>
				<Button
					onClick={() => (window.location.href = fallbackUrl)}
					variant="outline"
					size="lg"
				>
					{fallbackLabel}
				</Button>
			</div>

			{process.env.NODE_ENV === "development" && error.message && (
				<div className="mt-8 w-full max-w-2xl rounded-lg border border-red-500/20 bg-red-500/10 p-4">
					<p className="mb-2 font-mono text-sm font-semibold text-red-500">
						Development Error:
					</p>
					<p className="font-mono text-xs text-red-400">
						{error.message}
					</p>
					{error.digest && (
						<p className="mt-2 font-mono text-xs text-red-400">
							Digest: {error.digest}
						</p>
					)}
				</div>
			)}
		</div>
	);
}
